import React, {
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useContext,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { WhichDogSvg } from '@agria/theme/src/components/whichDog/WhichDog';
import { WhichCatSvg } from '@agria/theme/src/components/whichCat/WhichCat';
import britishEquestrianImageSrc from '@agria/theme/src/images/logos/british-equestrian-logo.png';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import { ImageProps, Image } from '../Image/Image';
import { Checkbox } from '../form/checkbox/Checkbox';
import { Typography } from '../typography/Typography';
import { useBackgroundColor } from '../../utils';
import { Stack } from '../stack/Stack';
import { getHighlightColor } from '../../utils/getHighlightColor';
import { Minus, Plus } from '../icon';
import { fontWeight } from '../../styles/tokens/typography';
import tokens from './tokens';

export interface SpeciesSelectorProps {
  image: ImageProps['src'];
  imageRight?: boolean;
  hideCheckbox?: boolean;
  species: {
    name: string;
    litterName: string;
    id: string;
  };
  subSpecies?: string;
  onChange?: (value: any) => void;
  value: Record<string, any>;
  isLitter?: boolean;
  isToggle?: boolean;
}

interface StyledSpeciesSelectorProps {
  isChecked?: boolean;
  imageRight?: boolean;
  AGRIA108?: string;
  AGRIA118?: string;
}

const StyleImage = styled(Image)`
  width: 100%;
  margin: 0 auto;
  text-align: center;
  transform: ${tokens.imageTransform};
  transition: ${tokens.imageTransition};
  align-self: end;
  pointer-events: none;
`;

const StyledSpeciesSelectorInner = styled.div<{ borderColor?: string }>`
  ${({ theme: { borderRadii, colors, maxMedia, space }, borderColor }) => css`
    border-radius: ${borderRadii[tokens.borderRadius]};
    display: grid;
    overflow: clip;

    ${borderColor &&
    css`
      @media (max-width: ${maxMedia.small}) {
        border-top: ${space.xxSmall} solid
          ${colors[borderColor as keyof typeof colors]};
      }
    `}
  `};
`;

const StyledSpeciesSelector = styled.div<StyledSpeciesSelectorProps>`
  ${({
    theme: { borderRadii, maxMedia, media, colors },
    isChecked,
    imageRight,
    AGRIA108,
    AGRIA118,
  }) => css`
    align-self: start;
    display: grid;
    position: relative;
    width: 100%;
    ${useBackgroundColor('utilityWhite')};
    border-radius: ${borderRadii[tokens.borderRadius]};
    transition: box-shadow 0.2s ease;

    ${AGRIA118 === 'variation' &&
    css`
      @media (max-width: ${media.small}) {
        margin-top: 28px;
      }
    `};

    ${AGRIA108 === 'variation' &&
    css`
      @media (min-width: ${media.small}) {
        margin-top: 48px;
      }
    `};

    ${isChecked && '--shadow: 4px 16px 32px -8px rgba(3, 36, 62, 0.16);'}

    box-shadow: var(--shadow, 0 0 transparent),
      var(--focus-outline, 0 0 transparent);

    @media (max-width: ${maxMedia.xSmall}) {
      align-items: flex-end;
    }

    &:hover {
      --shadow: 4px 16px 32px -8px rgba(3, 36, 62, 0.16);

      ${StyleImage} {
        transform: ${tokens.imageTransformHover};
        transition: ${tokens.imageTransitionHover};
      }
    }

    ${colors[tokens.focusWithin] &&
    css`
      &:focus-within {
        --focus-outline: 0 0 0 2px ${colors[tokens.focusOutlineColor]};
      }
    `}

    ${imageRight &&
    css`
      max-width: 542px;
      margin: 0 auto;

      ${StyledSpeciesSelectorInner} {
        grid-template-columns: 1fr 1fr;
      }
    `};
  `};
`;

const StyledHeader = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: ${space.xxSmall};
  `};
`;

const StyledBody = styled(Stack)<{ AGRIA108?: string; AGRIA118?: string }>`
  ${({ theme: { space, media }, AGRIA108, AGRIA118 }) => css`
    grid-row: 1;
    grid-column: 1;
    pointer-events: none;
    padding: ${space.small};

    @media (min-width: ${media.large}) {
      padding: ${space.large} ${space.large} ${space[tokens.paddingBottom]}
        ${space.large};
    }

    input,
    label {
      pointer-events: all;
    }

    ${AGRIA108 === 'variation' &&
    css`
      @media (min-width: ${media.small}) {
        padding-top: calc(${space.small} + 36px);
      }

      @media (min-width: ${media.large}) {
        padding-top: calc(${space.large} + 20px);
      }
    `};

    ${AGRIA118 === 'variation' &&
    css`
      @media (max-width: ${media.small}) {
        padding-top: 20px;
      }
    `};
  `};
`;

const StyledChangeSelector = styled.div`
  display: flex;
  pointer-events: all;
`;

const StyledChangeSelectorInput = styled.input`
  ${({ theme: { space, colors, media } }) => css`
    text-align: center;
    border-top: solid 2px ${colors[tokens.color]};
    border-bottom: solid 2px ${colors[tokens.color]};
    ${useBackgroundColor('utilityWhite')}
    padding: ${space.xxxSmall};
    min-height: 44px;
    border-radius: 0;
    width: 100%;
    -webkit-appearnce: none;

    @media (min-width: ${media.medium}) {
      padding: ${space.xSmall};
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  `};
`;

const StyledChangeSelectorButton = styled.button`
  ${({ theme: { space, colors, borderRadii, media } }) => css`
    flex-shrink: 0;
    display: grid;
    place-items: center;
    cursor: pointer;
    min-height: 44px;
    border: solid 2px;
    border-color: ${colors[tokens.color]};
    text-decoration: none;
    font-weight: ${fontWeight.bold};
    padding: ${space.xxxSmall};

    @media (min-width: ${media.medium}) {
      padding: ${space.xxSmall};
    }

    &:first-of-type {
      border-top-left-radius: ${borderRadii[tokens.borderRadius]};
      border-bottom-left-radius: ${borderRadii[tokens.borderRadius]};
    }

    &:last-of-type {
      border-top-right-radius: ${borderRadii[tokens.borderRadius]};
      border-bottom-right-radius: ${borderRadii[tokens.borderRadius]};
    }
  `};
`;

const StyledSpeciesSelectorButton = styled.button<{ imageRight?: boolean }>`
  ${({ imageRight }) => css`
    grid-column: ${imageRight ? '1 / span 2' : '1'};
    grid-row: ${imageRight ? '1' : '1 / span 2'};
  `};
`;

const StyledImageWrapper = styled.div<{ imageRight?: boolean }>`
  ${({ imageRight }) => css`
    grid-row: ${imageRight ? '1' : '2'};
    grid-column: ${imageRight ? '2' : '1'};
    pointer-events: none;
    align-self: end;
  `};
`;

const StyledCertification = styled.div<{
  AGRIA108?: string;
  AGRIA118?: string;
}>`
  ${({ theme: { maxMedia, media }, AGRIA108, AGRIA118 }) => css`
    position: absolute;
    top: 0;
    left: 50%;
    translate: -50% calc(-50% - 2px);
    width: max-content; // Prevent shrinking
    filter: drop-shadow(0 0 12px rgb(0 0 0 / 0.08));
    z-index: 1;
    pointer-events: none;

    ${AGRIA118 !== 'variation' &&
    css`
      @media (max-width: ${maxMedia.small}) {
        display: none;
      }
    `}

    ${AGRIA118 === 'variation' &&
    css`
      @media (max-width: ${maxMedia.small}) {
        svg {
          width: 76px;
          height: 60px;
        }
      }
    `}

    ${AGRIA108 !== 'variation' &&
    AGRIA118 === 'variation' &&
    css`
      @media (min-width: ${media.small}) {
        display: none;
      }
    `}
  `};
`;

const StyledBritishEquestrianLogo = styled.div<{ AGRIA118?: string }>`
  ${({ theme: { colors, maxMedia }, AGRIA118 }) => css`
    padding: 8px;
    box-shadow: inset 0 0 0 1px ${colors.neutral100};
    border-radius: 8px;
    background-color: ${colors.utilityWhite};

    ${AGRIA118 === 'variation' &&
    css`
      img {
        @media (max-width: ${maxMedia.small}) {
          width: 60px;
          height: 34px;
        }
      }
    `}
  `};
`;

export const SpeciesSelector = forwardRef<
  HTMLInputElement,
  SpeciesSelectorProps
>(
  (
    {
      image,
      imageRight,
      hideCheckbox,
      species,
      onChange,
      value,
      isLitter,
      isToggle,
      subSpecies,
    },
    ref
  ) => {
    const count: string | number = value[species.id]?.count; // empty string or number
    const checked = typeof count === 'string' || count > 0; // checked if '' or > 0
    const { experiments } = useContext(ExperimentsContext);

    const updateValue = (callback: (c: number | string) => number | string) => {
      let newValue;
      if (isToggle) {
        newValue = {
          [species.id]: {
            count: callback(count),
          },
        };
      } else {
        newValue = {
          ...value,
          [species.id]: {
            count: callback(count),
          },
        };
      }
      onChange?.(newValue);
    };

    const handleSelectorButtonClick = () => {
      updateValue((c) => (typeof c === 'string' || c > 0 ? 0 : 1));
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const allowedInputKeys =
        /^\d$|ArrowDown|ArrowLeft|ArrowRight|ArrowUp|Backspace|Clear|Control|Delete|End|Enter|Home|Redo|Shift|Super|Tab|Undo/;

      if (!allowedInputKeys.test(event.key)) {
        event.preventDefault();
      }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(event.target.value);

      // set to empty string if something weird, or 0 is entered when there's no way to re-check
      updateValue(() =>
        Number.isNaN(newValue) || (newValue === 0 && hideCheckbox)
          ? ''
          : newValue
      );
    };

    const handleIncrease = () => {
      updateValue((c) => (typeof c === 'string' ? 1 : c + 1));
    };

    const handleDecrease = () => {
      updateValue((c) => (typeof c === 'string' ? '' : Math.max(1, c - 1)));
    };

    const speciesNameKey = species.name.toLowerCase();
    const speciesName =
      isLitter && species.litterName
        ? species.litterName.toLowerCase()
        : species.name.toLowerCase();
    const speciesNameCased =
      isLitter && species.litterName ? species.litterName : species.name;

    const subSpeciesCased = subSpecies
      ? subSpecies.charAt(0).toUpperCase() + subSpecies.slice(1)
      : null;

    const speciesNameForLabel =
      subSpeciesCased || speciesNameCased.replace('Equine', 'horse');
    const speciesNameForLabelPlural = speciesNameForLabel
      .toLowerCase()
      .replace('puppy', 'puppie')
      .replace('equine', 'horse');

    return (
      <StyledSpeciesSelector
        isChecked={checked}
        imageRight={imageRight}
        data-test="species-selector"
        id={`speciesSelector-${speciesNameKey}`}
        AGRIA108={experiments.AGRIA108}
        AGRIA118={experiments.AGRIA118}
      >
        {(experiments.AGRIA108 === 'variation' ||
          experiments.AGRIA118 === 'variation') && (
          <StyledCertification
            AGRIA108={experiments.AGRIA108}
            AGRIA118={experiments.AGRIA118}
          >
            {
              {
                dog: <WhichDogSvg width={122} height={96} />,
                cat: <WhichCatSvg width={122} height={96} />,
                rabbit: null,
                equine: (
                  <StyledBritishEquestrianLogo AGRIA118={experiments.AGRIA118}>
                    <img
                      src={britishEquestrianImageSrc}
                      alt="British Equestrian Official Partner"
                      width={104}
                      height={64}
                    />
                  </StyledBritishEquestrianLogo>
                ),
              }[speciesNameKey]
            }
          </StyledCertification>
        )}
        <StyledSpeciesSelectorInner
          borderColor={
            experiments.AGRIA118 === 'variation'
              ? getHighlightColor(speciesNameKey)
              : undefined
          }
        >
          {!hideCheckbox && (
            <StyledSpeciesSelectorButton
              type="button"
              onClick={handleSelectorButtonClick}
              imageRight={imageRight}
              tabIndex={-1}
              aria-hidden="true"
            />
          )}

          <StyledBody
            gap={tokens.bodyGap}
            AGRIA108={experiments.AGRIA108}
            AGRIA118={experiments.AGRIA118}
          >
            <StyledHeader>
              <Typography
                variant="headingMedium"
                id={`speciesSelector-heading-${speciesNameKey}`}
              >
                {subSpeciesCased || speciesNameCased.replace('Equine', 'Horse')}
              </Typography>

              {!hideCheckbox && (
                <Checkbox
                  id={`species-${speciesNameKey}`}
                  checked={typeof count === 'string' || count > 0}
                  onChange={handleSelectorButtonClick}
                  ariaLabel={`Select to insure one or more ${speciesNameForLabelPlural}s`}
                />
              )}
            </StyledHeader>

            {checked && !isToggle && (
              <Stack gap={tokens.howManyPetsTitleGap}>
                <Typography
                  component="label"
                  htmlFor={`howManyPet-${speciesNameKey}`}
                  variant="bodyMedium"
                >
                  How many{' '}
                  {speciesName
                    .replace('puppy', 'puppie')
                    .replace('equine', 'horse')}
                  s?
                </Typography>

                <StyledChangeSelector>
                  <StyledChangeSelectorButton
                    id={`speciesSelector-decrease-${speciesNameKey}`}
                    type="button"
                    aria-label={`Decrease number of ${speciesNameForLabelPlural}s`}
                    aria-hidden="true"
                    tabIndex={-1}
                    data-test="decrease-button"
                    onClick={handleDecrease}
                    disabled={count === 1}
                  >
                    <Minus color={tokens.color} />
                  </StyledChangeSelectorButton>
                  <StyledChangeSelectorInput
                    value={count}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    id={`howManyPet-${speciesNameKey}`}
                    ref={ref}
                    type="number"
                    min={1}
                    max={20}
                    data-test="qty-input"
                    aria-live="assertive"
                  />
                  <StyledChangeSelectorButton
                    id={`speciesSelector-increase-${speciesNameKey}`}
                    data-test="increase-button"
                    type="button"
                    aria-label={`Increase number of ${speciesNameForLabelPlural}s`}
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={handleIncrease}
                  >
                    <Plus color={tokens.color} />
                  </StyledChangeSelectorButton>
                </StyledChangeSelector>
              </Stack>
            )}
          </StyledBody>

          <StyledImageWrapper imageRight={imageRight}>
            <StyleImage src={image} alt="" />
          </StyledImageWrapper>
        </StyledSpeciesSelectorInner>
      </StyledSpeciesSelector>
    );
  }
);

SpeciesSelector.displayName = 'SpeciesSelector';
